import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react'
import {
  CreateOrderRequest,
  Order,
  OrderFilterOptions,
  OrderStatistics,
  OrderStatus,
  OrderSubStatus,
  UpdateOrderRequest
} from '@/types/orders'
import {
  createOrder as createOrderInApi,
  deleteOrder as deleteOrderInApi,
  getOrderById as fetchOrderByIdFromApi,
  getOrders as fetchOrdersFromApi,
  updateOrder as updateOrderInApi,
  updateOrderStatus as updateOrderStatusInApi
} from '@/utils/orders'
import { useAuth } from '@/contexts/AuthContext'

interface OrderContextType {
  orders: Order[]
  loading: boolean
  error: string | null
  statistics: OrderStatistics
  fetchOrders: (filters?: OrderFilterOptions) => Promise<void>
  fetchOrderById: (id: string) => Promise<Order>
  createOrder: (orderData: CreateOrderRequest) => Promise<Order>
  updateOrder: (orderData: UpdateOrderRequest) => Promise<void>
  deleteOrder: (id: string) => Promise<void>
  getOrderById: (id: string) => Order | undefined
  updateOrderStatus: (orderId: string, status: OrderStatus, subStatus?: OrderSubStatus, notes?: string) => Promise<void>
  getOrdersByCustomer: (customerId: string) => Order[]
  getOrdersByBranch: (branchId: string) => Order[]
}

const EMPTY_STATISTICS: OrderStatistics = {
  totalOrders: 0,
  newOrders: 0,
  inProgressOrders: 0,
  readyForTrialOrders: 0,
  completedOrders: 0,
  deliveredOrders: 0
}

const OrderContext = createContext<OrderContextType | undefined>(undefined)

interface OrderProviderProps {
  children: ReactNode
}

export function OrderProvider({ children }: OrderProviderProps) {
  const { isAuthenticated } = useAuth()
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statistics, setStatistics] = useState<OrderStatistics>(EMPTY_STATISTICS)

  const fetchOrders = useCallback(async (filters?: OrderFilterOptions) => {
    if (!isAuthenticated) {
      return
    }

    setLoading(true)
    setError(null)
    try {
      const result = await fetchOrdersFromApi(filters)
      setOrders(result.orders)
      setStatistics(result.statistics)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch orders')
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const fetchOrderById = useCallback(async (id: string): Promise<Order> => {
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }

    setLoading(true)
    setError(null)
    try {
      const order = await fetchOrderByIdFromApi(id)
      setOrders((prev) => {
        const others = prev.filter((item) => item.id !== id)
        return [order, ...others]
      })
      return order
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch order'
      setError(message)
      throw new Error(message)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const createOrder = useCallback(async (orderData: CreateOrderRequest): Promise<Order> => {
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }

    setLoading(true)
    setError(null)
    try {
      const newOrder = await createOrderInApi(orderData)
      setOrders((prev) => [newOrder, ...prev])
      await fetchOrders()
      return newOrder
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create order'
      setError(message)
      throw new Error(message)
    } finally {
      setLoading(false)
    }
  }, [fetchOrders, isAuthenticated])

  const updateOrder = useCallback(async (orderData: UpdateOrderRequest) => {
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }

    setLoading(true)
    setError(null)
    try {
      const updatedOrder = await updateOrderInApi(orderData)
      setOrders((prev) => prev.map((order) => (order.id === updatedOrder.id ? updatedOrder : order)))
      await fetchOrders()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update order'
      setError(message)
      throw new Error(message)
    } finally {
      setLoading(false)
    }
  }, [fetchOrders, isAuthenticated])

  const deleteOrder = useCallback(async (id: string) => {
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }

    setLoading(true)
    setError(null)
    try {
      await deleteOrderInApi(id)
      setOrders((prev) => prev.filter((order) => order.id !== id))
      await fetchOrders()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete order'
      setError(message)
      throw new Error(message)
    } finally {
      setLoading(false)
    }
  }, [fetchOrders, isAuthenticated])

  const getOrderById = useCallback((id: string): Order | undefined => {
    return orders.find((order) => order.id === id)
  }, [orders])

  const updateOrderStatus = useCallback(async (
    orderId: string,
    status: OrderStatus,
    subStatus?: OrderSubStatus,
    _notes?: string
  ) => {
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }

    setLoading(true)
    setError(null)
    try {
      const updatedOrder = await updateOrderStatusInApi(orderId, status, subStatus)
      setOrders((prev) => prev.map((order) => (order.id === updatedOrder.id ? updatedOrder : order)))
      await fetchOrders()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update order status'
      setError(message)
      throw new Error(message)
    } finally {
      setLoading(false)
    }
  }, [fetchOrders, isAuthenticated])

  const getOrdersByCustomer = useCallback((customerId: string): Order[] => {
    return orders.filter((order) => order.customerId === customerId)
  }, [orders])

  const getOrdersByBranch = useCallback((branchId: string): Order[] => {
    return orders.filter((order) => order.branchId === branchId)
  }, [orders])

  useEffect(() => {
    if (!isAuthenticated) {
      setOrders([])
      setStatistics(EMPTY_STATISTICS)
      setError(null)
    }
  }, [isAuthenticated])

  const value: OrderContextType = {
    orders,
    loading,
    error,
    statistics,
    fetchOrders,
    fetchOrderById,
    createOrder,
    updateOrder,
    deleteOrder,
    getOrderById,
    updateOrderStatus,
    getOrdersByCustomer,
    getOrdersByBranch
  }

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  )
}

export function useOrders() {
  const context = useContext(OrderContext)
  if (context === undefined) {
    throw new Error('useOrders must be used within an OrderProvider')
  }
  return context
}
